import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

// ⌘K / Ctrl-K command palette (ADR 0160): jump to any admin section or cave
// without hunting the sidebar. Client-side over the already-loaded caves + the
// static section list — no new endpoint. Keyboard-first (↑/↓/Enter/Esc).

export interface Command {
  id: string;
  label: string;
  hint?: string;
  keywords?: string;
  run: () => void;
}

function score(cmd: Command, q: string): number {
  if (!q) return 1;
  const hay = `${cmd.label} ${cmd.hint ?? ""} ${cmd.keywords ?? ""}`.toLowerCase();
  const needle = q.toLowerCase();
  if (hay.includes(needle)) return 2;
  // subsequence match (fuzzy)
  let i = 0;
  for (const ch of hay) if (ch === needle[i]) i++;
  return i === needle.length ? 1 : 0;
}

export default function CommandPalette({ commands }: { commands: Command[] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global hotkey. Registered while the admin console is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      // focus after the paint
      const h = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(h);
    }
  }, [open]);

  const results = useMemo(() => {
    return commands
      .map((c) => ({ c, s: score(c, q.trim()) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.c)
      .slice(0, 12);
  }, [commands, q]);

  useEffect(() => setActive(0), [q]);

  if (!open) return null;

  const choose = (cmd: Command | undefined) => {
    if (!cmd) return;
    setOpen(false);
    cmd.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[active]);
    }
  };

  const rowStyle = (on: boolean): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    cursor: "pointer",
    background: on ? "var(--surface-3)" : "transparent",
    borderLeft: `2px solid ${on ? "var(--gold-500)" : "transparent"}`,
  });

  return (
    <div
      role="presentation"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        style={{
          width: "min(560px, 92vw)",
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Jump to a cave or section…"
          aria-label="Command palette search"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "16px 18px",
            fontSize: "var(--fs-lg)",
            color: "var(--text-1)",
            background: "transparent",
            border: "none",
            borderBottom: "1px solid var(--border)",
            outline: "none",
          }}
        />
        <ul style={{ listStyle: "none", margin: 0, padding: 4, maxHeight: "50vh", overflowY: "auto" }}>
          {results.length === 0 ? (
            <li style={{ padding: "14px", color: "var(--text-3)", fontSize: "var(--fs-md)" }}>No matches.</li>
          ) : (
            results.map((cmd, i) => (
              <li
                key={cmd.id}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(cmd)}
                aria-selected={i === active}
                style={rowStyle(i === active)}
              >
                <span style={{ fontSize: "var(--fs-md)", color: "var(--text-1)" }}>{cmd.label}</span>
                {cmd.hint && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--fs-2xs)",
                      textTransform: "uppercase",
                      letterSpacing: "var(--ls-label)",
                      color: "var(--text-4)",
                    }}
                  >
                    {cmd.hint}
                  </span>
                )}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
